import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  ShieldCheck, EnvelopeSimpleOpen, KeyReturn, Power, ArrowClockwise, User,
} from "@phosphor-icons/react";

const STATE_STYLES = {
  "Active": "bg-emerald-100 text-emerald-700",
  "Inactive": "bg-slate-200 text-slate-600",
  "Pending Activation": "bg-amber-100 text-amber-700",
  "Inactive (Pending)": "bg-slate-200 text-slate-600",
  "Not Created": "bg-slate-100 text-slate-500",
};

export default function CandidatePortalAccess({ candidateId, candidateEmail }) {
  const { user } = useAuth();
  const allowed = ["admin", "team_lead", "sdl"].includes(user?.role);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    if (!allowed) return;
    api.get(`/candidate-portal/status/${candidateId}`)
      .then((r) => setStatus(r.data))
      .catch(() => setStatus(null));
  }, [candidateId, allowed]);

  useEffect(() => { load(); }, [load]);

  if (!allowed) return null;

  const run = async (label, fn, successMsg) => {
    setBusy(label);
    try {
      const r = await fn();
      const emailErr = r?.data?.email_error;
      if (r?.data?.email_sent === false && emailErr) {
        toast.warning(`${successMsg} — email delivery warning: ${emailErr}`);
      } else {
        toast.success(successMsg);
      }
      load();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const state = status?.state || "Not Created";
  const cls = STATE_STYLES[state] || STATE_STYLES["Not Created"];

  const canCreate = state === "Not Created";
  const canResendActivation = state === "Pending Activation" || state === "Inactive (Pending)";
  const canReset = state === "Active" || state === "Inactive";
  const canDeactivate = state === "Active" || state === "Pending Activation";
  const canActivate = state === "Inactive" || state === "Inactive (Pending)";

  const fmt = (iso) => iso ? new Date(iso).toLocaleString() : "—";

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="portal-access-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-semibold flex items-center gap-2">
            <ShieldCheck size={16} /> Candidate Portal Access
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Manage the candidate&rsquo;s self-service portal login. Passwords are set by the candidate via a secure email link.
          </p>
        </div>
        <span className={`text-xs px-2.5 py-1 rounded-full ${cls}`} data-testid="portal-state-badge">{state}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4 text-sm">
        <Info label="Portal email" value={status?.email || candidateEmail || "—"} icon={User} />
        <Info label="Activated on" value={fmt(status?.activated_at)} icon={ShieldCheck} />
        <Info label="Last login" value={fmt(status?.last_login_at)} icon={KeyReturn} />
      </div>

      <div className="flex flex-wrap gap-2 mt-5">
        <Action disabled={!canCreate || busy} data-testid="portal-create-btn"
                onClick={() => run("create",
                  () => api.post(`/candidate-portal/create/${candidateId}`),
                  "Portal account created. Activation email sent to the candidate.")}
                icon={EnvelopeSimpleOpen} label="Create Portal Account" primary />
        <Action disabled={!canResendActivation || busy} data-testid="portal-resend-btn"
                onClick={() => run("resend",
                  () => api.post(`/candidate-portal/resend-activation/${candidateId}`),
                  "Activation email resent.")}
                icon={ArrowClockwise} label="Resend Activation Email" />
        <Action disabled={!canReset || busy} data-testid="portal-reset-btn"
                onClick={() => run("reset",
                  () => api.post(`/candidate-portal/reset-request/${candidateId}`),
                  "Password reset link emailed to the candidate.")}
                icon={KeyReturn} label="Send Password Reset Link" />
        {canActivate && (
          <Action disabled={!!busy} data-testid="portal-activate-btn"
                  onClick={() => run("activate",
                    () => api.post(`/candidate-portal/activate/${candidateId}`),
                    "Portal account reactivated.")}
                  icon={Power} label="Activate Account" />
        )}
        {canDeactivate && (
          <Action disabled={!!busy} data-testid="portal-deactivate-btn"
                  onClick={() => run("deactivate",
                    () => api.post(`/candidate-portal/deactivate/${candidateId}`),
                    "Portal account deactivated.")}
                  icon={Power} label="Deactivate Account" danger />
        )}
      </div>

      <p className="text-xs text-slate-400 mt-3">
        Activation and reset links expire in 60 minutes and are single-use. The candidate must click the link and set their own password.
      </p>
    </div>
  );
}

function Info({ label, value, icon: Icon }) {
  return (
    <div className="border border-slate-200 rounded-md px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1">
        <Icon size={11} /> {label}
      </div>
      <div className="text-sm text-slate-800 truncate">{value}</div>
    </div>
  );
}

function Action({ disabled, onClick, icon: Icon, label, primary, danger, ...rest }) {
  const base = "text-xs px-3 py-2 rounded-md flex items-center gap-1.5 transition-colors";
  const cls = disabled
    ? "border border-slate-200 text-slate-400 cursor-not-allowed"
    : primary
    ? "bg-sky-600 hover:bg-sky-700 text-white"
    : danger
    ? "border border-red-200 text-red-600 hover:bg-red-50"
    : "border border-slate-200 hover:bg-slate-50";
  return (
    <button onClick={onClick} disabled={disabled} className={`${base} ${cls}`} {...rest}>
      <Icon size={13} /> {label}
    </button>
  );
}
